import Constants from 'expo-constants';
import { useSelectedProfile } from 'hooks/useSelectedProfile';
import { createContext, useContext, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { cmsApiSlice } from 'services/cms';
import { Socket, io } from 'socket.io-client';
import { RootState } from 'store/index';
import { getSocketPlatform } from 'utils/socketPlatform';

type SocketWatchCallback = { watchedTime: number };

type SocketContextProps = {
  socket: Socket;
  watch: (data: PlayerProps) => Promise<SocketWatchCallback | undefined>;
};

const HEARTBEAT_INTERVAL_MS = 30_000;

const SocketContext = createContext({} as SocketContextProps);

const selectSocketUrl = (state: RootState) => {
  const globalConfigResult = cmsApiSlice.endpoints.getGlobalConfig.select(undefined)(state);
  const enableCmsProd = true;
  return enableCmsProd
    ? Constants.expoConfig?.extra?.socketUrl
    : Constants.expoConfig?.extra?.socketUrlHML;
};

export const SocketProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const auth = useSelector((state: RootState) => state.auth);
  const { selectedProfile } = useSelectedProfile();
  const socketUrl =
    useSelector(selectSocketUrl) ||
    Constants.expoConfig?.extra?.socketUrlHML ||
    Constants.expoConfig?.extra?.socketUrl ||
    '';

  const state = useMemo(() => {
    const socket: Socket = io(socketUrl, {
      autoConnect: false,
      transports: ['websocket'],
      auth: {
        token: auth.userToken,
        profile: selectedProfile?.id,
        platform: getSocketPlatform(),
      },
    });

    return {
      socket,
      async watch({ media, isLive }: PlayerProps) {
        return media
          ? await Promise.race<SocketWatchCallback>([
              new Promise<SocketWatchCallback>((resolve) => {
                socket.connect().emit(
                  'watch',
                  {
                    title: media.title,
                    image: media.image,
                    rating: media.ratingValue,
                    duration: media.duration,
                    trackId: media.videoTracks[0].trackId,
                    programId: media.programId,
                    programTitle: media.programTitle,
                    programUrl: media.programUrl,
                    mediaId: media.mediaId,
                    isLive: isLive ?? !media.duration,
                    user: auth?.id,
                    profile: selectedProfile?.id,
                  },
                  resolve
                );
              }),
              new Promise<SocketWatchCallback>((resolve) =>
                setTimeout(() => resolve({ watchedTime: 0 }), 2000)
              ),
            ])
          : undefined;
      },
    };
  }, [auth, selectedProfile?.id, socketUrl]);

  useEffect(() => {
    const { socket } = state;
    const tick = () => {
      if (socket.connected) socket.emit('heartbeat');
    };
    const interval = setInterval(tick, HEARTBEAT_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [state]);

  return <SocketContext.Provider value={state}>{children}</SocketContext.Provider>;
};

export const useSocket = () => useContext(SocketContext);
